import { ConsentType } from '@/features/dashboard/consentTemplates/types/ConsentTemplateType.ts';
import { AcceptanceDocumentVm } from '@/features/dashboard/bookingChanges/types/ChangePackageFlowViewModel.ts';
import { pluralize } from '@/utils/pluralize.ts';

/**
 * Dokumenty do ponownej akceptacji w kroku W2.
 *
 * Żaden dokument nie jest obowiązkowy — również ten, który na wyjeździe stoi
 * jako zgoda wymagana. Obowiązkowość dotyczy zapisu na wyjazd, a nie tego, co
 * paczka każe podpisać jeszcze raz; to jest decyzja operatora i wolno mu nie
 * wskazać niczego. System tylko podpowiada.
 *
 * Zaznaczenie żyje na stronie przepływu jako lista identyfikatorów, a nie jako
 * flaga przy dokumencie — dzięki temu lista z serwera zostaje nietknięta, a
 * całe „co idzie do podpisu” liczy się tutaj.
 */

/**
 * Podpowiedź systemu: zgoda z szablonu albo zgoda wymagana przy zapisie.
 *
 * Aneks powstaje ze złożenia treści szablonu z nowymi warunkami rezerwacji —
 * tylko taki dokument po zmianie brzmi inaczej niż ten podpisany przy zapisie.
 * Zgoda wymagana wchodzi obok tego, bo bez niej rezerwacja nie powstałaby
 * w ogóle, więc po zmianie warunków warto ją odnowić.
 *
 * Podpowiedź nie czyni dokumentu obowiązkowym — również wymagany da się
 * odznaczyć. Przyjmuje kształt cząstkowy, żeby tę samą regułę dało się zadać
 * zgodzie wyjazdu, zanim stanie się pozycją widoku.
 */
export const isDocumentRecommended = (
  document: Pick<AcceptanceDocumentVm, 'type' | 'required'>,
): boolean => document.type === ConsentType.TEMPLATE || document.required;

/** Zaznaczenie startowe: to, co system podpowiada. */
export const initialSelectedDocumentIds = (
  documents: AcceptanceDocumentVm[],
): string[] => documents.filter(isDocumentRecommended).map(({ id }) => id);

/** Czy dokument idzie do podpisu. */
export const isDocumentSelected = (
  document: AcceptanceDocumentVm,
  selectedIds: string[],
): boolean => selectedIds.includes(document.id);

/** Przełączenie dokumentu — każdego, łącznie z podpowiedzianym. */
export const toggleDocumentSelection = (
  selectedIds: string[],
  document: AcceptanceDocumentVm,
): string[] =>
  selectedIds.includes(document.id)
    ? selectedIds.filter((id) => id !== document.id)
    : [...selectedIds, document.id];

export const countDocumentsToSign = (
  documents: AcceptanceDocumentVm[],
  selectedIds: string[],
): number =>
  documents.filter((document) => isDocumentSelected(document, selectedIds))
    .length;

/** Licznik przy nagłówku bloku dokumentów: „2 z 4 do podpisu”. */
export const documentsToSignLabel = (
  documents: AcceptanceDocumentVm[],
  selectedIds: string[],
): string => {
  const count = countDocumentsToSign(documents, selectedIds);
  if (count === 0) {
    return 'nic do podpisu';
  }
  return `${count} z ${documents.length} do podpisu`;
};

/**
 * Zdanie o odstępstwie od podpowiedzi; `null`, gdy odstępstwa nie ma.
 *
 * Odznaczona podpowiedź nie jest błędem — operator może wiedzieć lepiej — ale
 * ma zostać wypowiedziana, bo klient dostanie do podpisu mniej, niż wynika ze
 * zmiany.
 */
export const skippedRecommendationLabel = (
  documents: AcceptanceDocumentVm[],
  selectedIds: string[],
): string | null => {
  const skipped = documents.filter(
    (document) =>
      isDocumentRecommended(document) &&
      !isDocumentSelected(document, selectedIds),
  ).length;

  if (skipped === 0) {
    return null;
  }
  return `Zapisujący nie podpisze ${pluralize(
    skipped,
    'zalecanego dokumentu',
    'zalecanych dokumentów',
    'zalecanych dokumentów',
  )}.`;
};
